/**
 * i18n provider + `useTranslation` hook.
 *
 * Dependency-free (no i18next) to keep the bundle lean while we ship
 * English-only. `t(key, vars?)` looks the key up in the active locale's
 * catalog, interpolates `{var}` placeholders, and falls back to the key itself
 * (so a missing translation is visible, never a blank). Locale is persisted to
 * localStorage and can be switched once additional catalogs are added.
 */
import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react'
import { messages, type Locale } from './messages'

interface I18nContextType {
  locale: Locale
  setLocale: (l: Locale) => void
  t: (key: string, vars?: Record<string, string | number>) => string
}

const I18nContext = createContext<I18nContextType | undefined>(undefined)

const STORAGE_KEY = 'locale'

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template
  return template.replace(/\{(\w+)\}/g, (_, name) =>
    name in vars ? String(vars[name]) : `{${name}}`
  )
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const stored = (typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY)) as Locale | null
    return stored && stored in messages ? stored : 'en'
  })

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l)
    try {
      localStorage.setItem(STORAGE_KEY, l)
    } catch {
      /* ignore persistence errors (private mode, etc.) */
    }
  }, [])

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => {
      const catalog = messages[locale] || messages.en
      const template = catalog[key] ?? messages.en[key] ?? key
      return interpolate(template, vars)
    },
    [locale]
  )

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t])
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useTranslation(): I18nContextType {
  const ctx = useContext(I18nContext)
  if (!ctx) {
    // Safe fallback so components don't crash if rendered outside the provider
    // (e.g. isolated tests): identity translation.
    return {
      locale: 'en',
      setLocale: () => {},
      t: (key: string, vars?: Record<string, string | number>) => {
        const template = messages.en[key] ?? key
        return interpolate(template, vars)
      },
    }
  }
  return ctx
}
